/* ═══════════════════════════════════════════════════════════════
   PiARIS — Shared UI Components
   Reusable React components used across all products
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P, PI_LOGO, SC } from "./constants";
import { kbNav } from "./helpers";

var E = React.createElement;

/* PiLogo — Pi Consultants logo, solid dark indigo on transparent PNG.
   No filters needed — color and transparency baked into image. */
function PiLogo(p){
  var sz=p.size||40,op=p.opacity||1;
  return E("img",{src:PI_LOGO,alt:"Pi Consultants","aria-hidden":"true",style:{
    height:sz,objectFit:"contain",display:"inline-block",verticalAlign:"middle",
    opacity:op
  }});
}

/* Integrated Pi+ARIS lockup — ARIS tucked close so Pi's roof meets the A */
function PiArisLockup(p){
  var sz=p.size||"large";
  var piH=sz==="small"?32:sz==="med"?44:64;
  var txtSz=sz==="small"?18:sz==="med"?24:42;
  var overlap=sz==="small"?-8:sz==="med"?-12:-18;
  var img=E("img",{src:PI_LOGO,alt:"","aria-hidden":"true",style:{
      height:piH,objectFit:"contain",display:"inline-block",verticalAlign:"middle",
      opacity:p.opacity||1,marginRight:overlap
    }});
  var txt=E("span",{style:{fontSize:txtSz,fontWeight:900,color:P.slate,letterSpacing:"-0.5px",lineHeight:1}},"ARIS");
  var sty={display:"inline-flex",alignItems:"center",gap:0,lineHeight:1};
  /* When `home`, the lockup is itself a link to the site root. The nav
     leaves it plain (its wrapping control already goes home) to avoid a
     link nested inside a clickable. */
  if(p.home) return E("a",{href:"/","aria-label":"PiARIS home",title:"Home",style:Object.assign({textDecoration:"none",cursor:"pointer"},sty)},img,txt);
  return E("div",{style:sty,"aria-label":"PiARIS"},img,txt);
}

/* A11Y themed SVG illustrations for homepage */
function A11yIcon(p){
  var sz=p.size||48,type=p.type||"eye";
  var col1=p.c1||P.pri,col2=p.c2||P.accent;
  if(type==="eye") return E("svg",{width:sz,height:sz,viewBox:"0 0 48 48","aria-hidden":"true"},
    E("circle",{cx:24,cy:24,r:20,fill:col1+"10",stroke:col1+"30",strokeWidth:1.5}),
    E("path",{d:"M8 24c4-8 10-12 16-12s12 4 16 12c-4 8-10 12-16 12S12 32 8 24z",fill:"none",stroke:col1,strokeWidth:2.5,strokeLinejoin:"round"}),
    E("circle",{cx:24,cy:24,r:5,fill:col1}),
    E("circle",{cx:22,cy:22,r:1.5,fill:"#fff"}),
    E("path",{d:"M6 6l36 36",stroke:col2,strokeWidth:2,strokeLinecap:"round",opacity:.3}));
  if(type==="keyboard") return E("svg",{width:sz,height:sz,viewBox:"0 0 48 48","aria-hidden":"true"},
    E("rect",{x:4,y:14,width:40,height:24,rx:4,fill:col1+"10",stroke:col1+"30",strokeWidth:1.5}),
    [10,18,26,34].map(function(x,i){return E("rect",{key:i,x:x,y:19,width:4,height:4,rx:1,fill:col1,opacity:.6})}),
    [10,18,26,34].map(function(x,i){return E("rect",{key:"b"+i,x:x,y:26,width:4,height:4,rx:1,fill:col1,opacity:.4})}),
    E("rect",{x:14,y:33,width:20,height:3,rx:1.5,fill:col2,opacity:.5}),
    E("rect",{x:8,y:18,width:6,height:6,rx:1.5,fill:"none",stroke:col2,strokeWidth:2,strokeDasharray:"2 1"}));
  if(type==="screen") return E("svg",{width:sz,height:sz,viewBox:"0 0 48 48","aria-hidden":"true"},
    E("rect",{x:6,y:6,width:36,height:28,rx:3,fill:col1+"10",stroke:col1+"30",strokeWidth:1.5}),
    E("path",{d:"M18 38h12 M24 34v4",stroke:col1,strokeWidth:2,strokeLinecap:"round"}),
    E("circle",{cx:24,cy:20,r:6,fill:"none",stroke:col2,strokeWidth:2}),
    E("path",{d:"M24 14v12M18 20h12",stroke:col2,strokeWidth:1.5,strokeLinecap:"round",opacity:.5}),
    [14,20,26,32].map(function(x,i){return E("path",{key:i,d:"M"+x+" 10c0 2 2 3 4 3s4-1 4-3",fill:"none",stroke:col1,strokeWidth:1,opacity:.3})}));
  if(type==="doc") return E("svg",{width:sz,height:sz,viewBox:"0 0 48 48","aria-hidden":"true"},
    E("path",{d:"M12 4h18l10 10v30a2 2 0 01-2 2H12a2 2 0 01-2-2V6a2 2 0 012-2z",fill:col1+"10",stroke:col1+"30",strokeWidth:1.5}),
    E("path",{d:"M30 4v10h10",fill:"none",stroke:col1+"30",strokeWidth:1.5}),
    [16,16,16,16].map(function(_,i){return E("rect",{key:i,x:16,y:20+i*6,width:i<3?16:10,height:2,rx:1,fill:col1,opacity:.3+i*.1})}),
    E("circle",{cx:34,cy:36,r:8,fill:col2,opacity:.15}),
    E("path",{d:"M31 36l2 2 4-4",fill:"none",stroke:col2,strokeWidth:2,strokeLinecap:"round",strokeLinejoin:"round"}));
  if(type==="shield") return E("svg",{width:sz,height:sz,viewBox:"0 0 48 48","aria-hidden":"true"},
    E("path",{d:"M24 4L8 12v12c0 10 7 19 16 22 9-3 16-12 16-22V12L24 4z",fill:col1+"10",stroke:col1,strokeWidth:2}),
    E("path",{d:"M18 24l4 4 8-8",fill:"none",stroke:col2,strokeWidth:3,strokeLinecap:"round",strokeLinejoin:"round"}));
  if(type==="people") return E("svg",{width:sz,height:sz,viewBox:"0 0 48 48","aria-hidden":"true"},
    E("circle",{cx:24,cy:14,r:6,fill:col1+"30"}),
    E("path",{d:"M14 34c0-6 4-10 10-10s10 4 10 10",fill:col1+"15",stroke:col1,strokeWidth:2}),
    E("circle",{cx:38,cy:16,r:4,fill:col2+"30"}),
    E("path",{d:"M32 32c0-4 2-7 6-7s6 3 6 7",fill:col2+"15",stroke:col2,strokeWidth:1.5}),
    E("circle",{cx:10,cy:16,r:4,fill:col2+"30"}),
    E("path",{d:"M4 32c0-4 2-7 6-7s6 3 6 7",fill:col2+"15",stroke:col2,strokeWidth:1.5}),
    E("path",{d:"M10 40h28",stroke:col1,strokeWidth:1.5,strokeDasharray:"3 2",opacity:.3}));
  return null;
}

/* ── Shared UI Components ── */
/** Toggle pill button — used for filters and tab selectors */
function Pill(p){
  var a=p.on,ac=p.c||P.pri;
  return E("button",{onClick:p.onClick,"aria-current":p["aria-current"]||undefined,"aria-pressed":a?"true":"false",style:Object.assign({padding:"6px 14px",borderRadius:8,border:"1.5px solid "+(a?ac:P.bdr),background:a?(ac+"12"):P.card,color:a?ac:P.slateM,fontSize:11,fontWeight:a?700:500},p.sx||{})},p.children);
}
/** Severity Badge — colored pill with dot indicator. Props: {s:"critical"|"major"|"minor", sm:boolean} */
function SevB(p){
  var c=SC[p.s]||SC.minor;
  return E("span",{style:{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:6,background:c[1],color:c[0],fontSize:p.sm?8:9.5,fontWeight:700,textTransform:"uppercase"}},
    E("span",{style:{width:5,height:5,borderRadius:"50%",background:c[0]}}),p.s);
}
/** Score Ring — SVG donut chart. Auto-colors based on compliance thresholds. Props: {v:0-100, sz, sw} */
function Ring(p){
  var v=p.v,sz=p.sz||80,sw=p.sw||6,r=(sz-sw)/2,ci=2*Math.PI*r,col=v>=95?P.emerald:v>=85?P.accent:v>=70?P.amber:v>=50?P.coral:P.sCrit;
  return E("svg",{width:sz,height:sz,style:{transform:"rotate(-90deg)"}},
    E("circle",{cx:sz/2,cy:sz/2,r:r,fill:"none",stroke:P.bdr,strokeWidth:sw}),
    E("circle",{cx:sz/2,cy:sz/2,r:r,fill:"none",stroke:col,strokeWidth:sw,strokeDasharray:ci,strokeDashoffset:ci-(v/100)*ci,strokeLinecap:"round",style:{transition:"stroke-dashoffset .6s"}}));
}
/** Card — base surface component with border, shadow, optional click. Props: {pad, sx, onClick, children} */
function Card(p){var isClickable=!!p.onClick;var kb=isClickable?kbNav(p.onClick):{};return E("div",Object.assign({},kb,{"aria-label":p["aria-label"]||undefined,style:Object.assign({padding:p.pad||16,borderRadius:12,background:P.card,border:"1.5px solid "+P.bdr,boxShadow:"0 1px 3px rgba(0,0,0,.04)"},isClickable?{cursor:"pointer"}:{},p.sx||{})}),p.children);}
/** Metric Card — centered number + label, used on Dashboard. Props: {v:value, label, c:color, onClick, sx} */
function MC(p){return E(Card,{sx:Object.assign({cursor:p.onClick?"pointer":"default"},p.sx||{}),onClick:p.onClick,"aria-label":p.label+": "+p.v},E("div",{style:{fontSize:26,fontWeight:800,color:p.c||P.slate,textAlign:"center"}},p.v),E("div",{style:{fontSize:10,color:P.slateL,marginTop:4,fontWeight:600,textTransform:"uppercase",textAlign:"center",letterSpacing:".04em"}},p.label));}


export { E, PiLogo, PiArisLockup, A11yIcon, Pill, SevB, Ring, Card, MC };
